import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import i18n from '../i18n';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (credentials) => {
    const response = await authAPI.login(credentials);
    localStorage.setItem('token', response.data.token);
    const userData = response.data.user;
    setUser(userData);
    // Set language from user profile
    if (userData.language) {
      i18n.changeLanguage(userData.language);
      localStorage.setItem('language', userData.language);
    }
    return response.data;
  }, []);

  const checkAuth = useCallback(async () => {
    // Check if we have a token and validate it with backend
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getProfile();
        const userData = response.data;
        setUser(userData);
        // Set language from user profile
        if (userData.language) {
          i18n.changeLanguage(userData.language);
          localStorage.setItem('language', userData.language);
        }
        setLoading(false);
        return;
      } catch (error) {
        // Token is invalid or expired, clear it
        localStorage.removeItem('token');
      }
    }

    // In dev mode, auto-login if credentials are provided
    if (process.env.NODE_ENV === 'development') {
      const devUsername = process.env.REACT_APP_DEV_USERNAME;
      const devPassword = process.env.REACT_APP_DEV_PASSWORD;

      if (devUsername && devPassword && devPassword !== 'your_password_here') {
        try {
          console.log('[Dev Mode] Auto-logging in with credentials...');
          await login({ username: devUsername, password: devPassword });
        } catch (error) {
          console.error('[Dev Mode] Auto-login failed:', error.response?.data?.error || error.message);
        }
      }
    }

    setLoading(false);
  }, [login]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const registerWithInvite = async (userData) => {
    const response = await authAPI.registerWithInvite(userData);
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await authAPI.forgotPassword({ email });
    return response.data;
  };

  const resetPassword = async (token, password) => {
    const response = await authAPI.resetPassword({ token, password });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('familyTreeCache'); // Clear data cache on logout
    setUser(null);
  };

  const updateSettings = async (settings) => {
    const response = await authAPI.updateSettings(settings);
    setUser(response.data.user);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, registerWithInvite, forgotPassword, resetPassword, logout, updateSettings }}>
      {children}
    </AuthContext.Provider>
  );
};
